import { useEffect, useRef } from "react";

import { datetime2String, isFunction, mergeDeep } from "../../utils/utils";
import useStorageState from "../useStorageState";

export default function useWallboxData(
  locationClient,
  showMsg = null,
  updateInterval = 60000,
  isFocused = true,
  storage = null,
) {
  const [wallboxes, saveWallboxes, setWallboxes] = useStorageState(
    storage,
    "wallboxes",
    null,
  );

  const initialized = useRef(false);
  const interval = useRef(null);

  const resetWallboxes = async () => {
    try {
      const response = await locationClient.getWallboxes(true);
      saveWallboxes(response.data);
      return true;
    } catch (error) {
      console.warn('Error in "resetWallboxes"', error, error.stack);
      return false;
    }
  };

  const updateWallboxes = async () => {
    try {
      const response = await locationClient.getWallboxes(true);
      saveWallboxes((ws) => {
        return ws.map((w) => {
          const newData = response.data.find((_w) => _w.id === w.id) || {};
          return { ...w, ...newData };
        });
      });
      return true;
    } catch (error) {
      console.warn('Error in "updateWallboxes"', error, error.stack);
      return false;
    }
  };

  const initData = async () => {
    await resetWallboxes();
    initialized.current = true;
  };

  const loadData = updateWallboxes;

  const clearData = () => {
    setWallboxes(null);
    initialized.current = false;
  };

  useEffect(() => {
    if (isFocused) initData();
    else clearData();
  }, [isFocused]);

  useEffect(() => {
    if (initialized.current && updateInterval) {
      startRecurrentUpdate(updateInterval);
      return stopRecurrentUpdate; // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
    }
    if (!initialized.current) stopRecurrentUpdate();
  }, [initialized.current]);

  const startRecurrentUpdate = (intervalTime) => {
    if (!interval.current)
      interval.current = setInterval(() => loadData(), intervalTime);
  };

  const stopRecurrentUpdate = () => {
    if (interval.current) {
      clearInterval(interval.current);
      interval.current = null;
    }
  };

  const _updateWallbox = (wallboxId, update) => {
    saveWallboxes((ws) =>
      ws.map((w) =>
        w.id === wallboxId
          ? mergeDeep(w, isFunction(update) ? update(w) : update)
          : w,
      ),
    );
  };

  const _updateWallboxSetPoint = (setPointId, update) => {
    saveWallboxes((ws) =>
      ws.map((w) =>
        w._set_point?.id === setPointId
          ? mergeDeep(w, { _set_point: update })
          : w,
      ),
    );
  };

  const _updateWallboxRequest = async (
    wallbox,
    update,
    successMsg,
    errorMsg,
  ) => {
    _updateWallbox(wallbox.id, update); // already set to prevent flicker
    try {
      const response = await locationClient.putWallbox(wallbox.id, update);
      _updateWallbox(wallbox.id, response.data);
      successMsg && showMsg?.(successMsg);
      return true;
    } catch (error) {
      console.warn('Error in "_updateWallboxRequest"', error, error.stack);
      _updateWallbox(wallbox.id, wallbox);
      errorMsg && showMsg?.(errorMsg, "error");
      return false;
    }
  };

  const _updateWallboxSetPointRequest = async (
    wallbox,
    update,
    successMsg,
    errorMsg,
  ) => {
    const setpoint = wallbox._set_point;
    _updateWallboxSetPoint(setpoint.id, update); // already set to prevent flicker
    try {
      const response = await locationClient.putWallboxSetPoint(
        setpoint.id,
        update,
      );
      _updateWallboxSetPoint(setpoint.id, response.data);
      successMsg && showMsg?.(successMsg);
      return true;
    } catch (error) {
      console.warn(
        'Error in "_updateWallboxSetPointRequest"',
        error,
        error.stack,
      );
      _updateWallboxSetPoint(setpoint.id, setpoint);
      errorMsg && showMsg?.(errorMsg, "error");
      return false;
    }
  };

  const handleWallboxLockChange = async (wallbox, locked) => {
    return _updateWallboxSetPointRequest(
      wallbox,
      { locked },
      "Die Wallbox wurde " + (locked ? "gesperrt!" : "enstperrt!"),
      "Die Wallbox konnte nicht " +
        (locked ? "gesperrt!" : "enstperrt") +
        " werden!",
    );
  };

  const handleWallboxSurplusChange = async (wallbox, surplusCharging) => {
    return _updateWallboxSetPointRequest(
      wallbox,
      { surplus_charging: surplusCharging },
      "PV-Überschussladen wurde  " +
        (surplusCharging ? "aktiviert!" : "deaktiviert!"),
      "PV-Überschussladen konnte nicht " +
        (surplusCharging ? "aktiviert" : "deaktiviert") +
        "werden!",
    );
  };

  const handleWallboxHybridChange = async (wallbox, hybridChargingCurrent) => {
    const setpoint = wallbox._set_point;
    const messaging =
      !!setpoint.hybrid_charging_current !== !!hybridChargingCurrent;
    return _updateWallboxSetPointRequest(
      wallbox,
      { hybrid_charging_current: hybridChargingCurrent },
      messaging &&
        "PV-Hybridladen wurde  " +
          (hybridChargingCurrent ? "aktiviert!" : "deaktiviert!"),
      messaging &&
        "PV-Hybridladen konnte nicht " +
          (hybridChargingCurrent ? "aktiviert" : "deaktiviert") +
          "werden!",
    );
  };

  const handleWallboxNameChange = async (wallbox, name) => {
    return _updateWallboxRequest(
      wallbox,
      { name },
      "Name geändert!",
      "Name konnte nicht geändert werden!",
    );
  };

  const handleWallboxLockTimeAdding = async (wallbox, locked, datetime) => {
    try {
      const response = await locationClient.addWallboxLockTime({
        wallbox: wallbox.id,
        locked,
        datetime: datetime2String(datetime),
      });
      _updateWallbox(wallbox.id, (w) => ({
        _lock_times: [...w._lock_times, response.data],
      }));
      showMsg?.(`${locked ? "Sperrzeit" : "Entsperrzeit"} hinzugefügt!`);
      return true;
    } catch (error) {
      console.warn(
        'Error in "handleWallboxLockTimeAdding"',
        error,
        error.stack,
      );
      showMsg?.(
        `${locked ? "Sperrzeit" : "Entsperrzeit"} konnte nicht hinzugefügt werden!`,
        "error",
      );
      return false;
    }
  };

  const handleWallboxLockTimeDeletion = async (wallbox, lockTime) => {
    try {
      await locationClient.deleteWallboxLockTime(lockTime.id);
      _updateWallbox(wallbox.id, (w) => ({
        _lock_times: w._lock_times.filter((s) => s.id !== lockTime.id),
      }));
      showMsg?.(`${lockTime.locked ? "Sperrzeit" : "Entsperrzeit"} gelöscht!`);
      return true;
    } catch (error) {
      console.warn(
        'Error in "handleWallboxLockTimeAdding"',
        error,
        error.stack,
      );
      showMsg?.(
        `${lockTime.locked ? "Sperrzeit" : "Entsperrzeit"} konnte nicht gelöscht werden!`,
        "error",
      );
      return false;
    }
  };

  return {
    wallboxes,
    handleWallboxLockChange,
    handleWallboxSurplusChange,
    handleWallboxHybridChange,
    handleWallboxLockTimeAdding,
    handleWallboxLockTimeDeletion,
    handleWallboxNameChange:
      wallboxes && wallboxes.length > 1 ? handleWallboxNameChange : null,
    data: { update: loadData, clear: clearData, init: initData },
    recurrentUpdate: { start: startRecurrentUpdate, stop: stopRecurrentUpdate },
  };
}
